import { useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
  WifiOff,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { SocialPlatform, SourceTier } from '@/client/types/media';
import { ALL_SOCIAL_PLATFORMS, PLATFORM_LABEL } from '@/client/types/media';
import type { ApiSourceAccount } from '@/client/types/marketing/api';
import Pagination from '@/client/components/common/Pagination';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ChipMultiSelect } from '@/client/pages/Admin/shared/ChipMultiSelect';
import { MediaPageShell } from './components/MediaPageShell';
import { PlatformLogoBadge } from './components/PlatformLogo';
import { AccountFormDialog } from './components/AccountFormDialog';
import { SourceContactsCell } from './components/SourceContactsCell';
import { ImportProviderAccountsDialog } from './components/ImportProviderAccountsDialog';
import { ActionIconButton } from './components/ActionIconButton';
import { Link } from 'react-router-dom';
import { formatCompact } from './fixtures';
import {
  useDeleteSourceAccountMutation,
  useSourceAccountsQuery,
  useSourceCoverageCountsQuery,
} from '@/client/api/marketing/sources';
import { getApiErrorMessage } from '@/client/utils/errors';

/**
 * Sources → Accounts, on real data.
 *
 * Rows are keyed on `said`, an integer primary key, not on an `acc_*` string —
 * this page settled that question for the module, and `types/marketing/sources.ts`
 * carries the (kind, id) pair everything else now uses.
 *
 * The Coverage column comes from a single aggregate rather than a count per row.
 * Fifty visible accounts would otherwise mean fifty round trips against the
 * largest collection in the module to render one number each.
 */

const PLATFORMS: SocialPlatform[] = [...ALL_SOCIAL_PLATFORMS];

const PLATFORM_CHIP_OPTIONS = PLATFORMS.map((p) => ({
  value: p,
  label: PLATFORM_LABEL[p],
}));

const OWNERSHIP_OPTIONS = [
  { value: 'all', label: 'All ownership' },
  { value: 'owned', label: 'Owned' },
  { value: 'partner_influencer', label: 'Partner influencer' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'other', label: 'Other' },
] as const;

type OwnershipFilter = (typeof OWNERSHIP_OPTIONS)[number]['value'];
type ScopeFilter = 'all' | 'owned' | 'tracked';
type RoleFilter = 'all' | 'destination' | 'listen' | 'both' | 'neither';
type ActiveFilter = 'all' | 'active' | 'inactive';
type TierFilter = 'all' | SourceTier;
type SortKey = 'name' | 'platform' | 'type' | 'tier' | 'followers' | 'coverage';
type SortDir = 'asc' | 'desc';

/** The API stores platform as a free string; fall back rather than crash. */
const platformLabel = (platform: string) =>
  PLATFORM_LABEL[platform as SocialPlatform] ?? platform;

const VERIFY_BADGE: Record<string, { label: string; className: string }> = {
  exists: { label: 'Handle found', className: 'bg-emerald-50 text-emerald-700' },
  connected: { label: 'Connected', className: 'bg-emerald-50 text-emerald-700' },
  not_found: { label: 'Not found', className: 'bg-red-50 text-red-700' },
  // Not the same as not_found: the check itself failed. Saying "not found"
  // here would report a network problem as a dead handle.
  indeterminate: { label: 'Check failed', className: 'bg-amber-50 text-amber-800' },
  expired: { label: 'Expired', className: 'bg-amber-50 text-amber-800' },
};

const selectClass =
  'h-9 rounded-md border border-gray-200 bg-white px-2 text-sm outline-none ring-blue-500 focus:ring-2';

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1 rounded hover:text-gray-800',
          active && 'text-gray-900',
        )}
      >
        {label}
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
      </button>
    </th>
  );
}

function FilterToggleGroup<T extends string>({
  value,
  onChange,
  options,
  'aria-label': ariaLabel,
}: {
  value: T;
  onChange: (next: T) => void;
  options: { value: T; label: string }[];
  'aria-label'?: string;
}) {
  return (
    <div
      className="flex rounded-md border border-gray-200 bg-white p-0.5"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            'rounded px-2.5 py-1.5 text-xs font-medium transition-colors',
            value === opt.value
              ? 'bg-gray-900 text-white'
              : 'text-gray-600 hover:bg-gray-50',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

const SourcesAccountsPage = () => {
  const { data, isLoading, isError, error } = useSourceAccountsQuery({ limit: 200 });
  const coverageCounts = useSourceCoverageCountsQuery();
  const removeAccount = useDeleteSourceAccountMutation();

  const [q, setQ] = useState('');
  const [platforms, setPlatforms] = useState<SocialPlatform[]>([]);
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [role, setRole] = useState<RoleFilter>('all');
  const [tier, setTier] = useState<TierFilter>('all');
  const [active, setActive] = useState<ActiveFilter>('all');
  const [ownership, setOwnership] = useState<OwnershipFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ApiSourceAccount | null>(null);
  const [deleting, setDeleting] = useState<ApiSourceAccount | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const accounts: ApiSourceAccount[] = data?.items ?? [];
  const coverageMap = coverageCounts.data?.accounts;

  const coverageFor = (said: number) => coverageMap?.[String(said)] ?? 0;

  const filtersActive =
    q.trim().length > 0 ||
    platforms.length > 0 ||
    scope !== 'all' ||
    role !== 'all' ||
    tier !== 'all' ||
    active !== 'all' ||
    ownership !== 'all';

  const clearFilters = () => {
    setQ('');
    setPlatforms([]);
    setScope('all');
    setRole('all');
    setTier('all');
    setActive('all');
    setOwnership('all');
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Followers / coverage / tier default high→low; names A→Z.
      setSortDir(key === 'followers' || key === 'coverage' || key === 'tier' ? 'desc' : 'asc');
    }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const platformSet = new Set(platforms);

    const rows = accounts.filter((a) => {
      if (platformSet.size > 0 && !platformSet.has(a.platform as SocialPlatform)) return false;

      if (scope === 'owned' && !a.owned) return false;
      if (scope === 'tracked' && a.owned) return false;

      if (role === 'destination' && !a.use_as_destination) return false;
      if (role === 'listen' && !a.use_as_listen) return false;
      if (role === 'both' && !(a.use_as_destination && a.use_as_listen)) return false;
      if (role === 'neither' && (a.use_as_destination || a.use_as_listen)) return false;

      if (tier !== 'all' && a.tier !== tier) return false;

      if (active === 'active' && !a.active) return false;
      if (active === 'inactive' && a.active) return false;

      if (ownership !== 'all') {
        const own = (a.ownership || (a.owned ? 'owned' : 'other')).toLowerCase();
        if (own !== ownership) return false;
      }

      if (query) {
        const topics = Array.isArray(a.topics) ? (a.topics as string[]) : [];
        const haystack = [
          a.display_name,
          a.handle,
          a.owner_name,
          a.region,
          a.notes,
          a.contact_email,
          a.ownership,
          platformLabel(a.platform),
          a.verify_state,
          ...topics,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      return true;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'platform':
          cmp = platformLabel(a.platform).localeCompare(platformLabel(b.platform));
          break;
        case 'type':
          cmp = Number(!!b.owned) - Number(!!a.owned);
          break;
        case 'tier':
          cmp = (a.tier ?? 99) - (b.tier ?? 99);
          break;
        case 'followers':
          cmp = (a.followers ?? -1) - (b.followers ?? -1);
          break;
        case 'coverage':
          cmp =
            (coverageMap?.[String(a.said)] ?? 0) - (coverageMap?.[String(b.said)] ?? 0);
          break;
        case 'name':
        default:
          cmp = a.display_name.localeCompare(b.display_name);
          break;
      }
      if (cmp !== 0) return cmp * dir;
      return a.display_name.localeCompare(b.display_name);
    });

    return rows;
  }, [
    accounts,
    platforms,
    scope,
    role,
    tier,
    active,
    ownership,
    q,
    sortKey,
    sortDir,
    coverageMap,
  ]);

  useEffect(() => {
    setPage(1);
  }, [q, platforms, scope, role, tier, active, ownership, sortKey, sortDir]);

  useEffect(() => {
    const max = Math.max(1, Math.ceil(filtered.length / limit) || 1);
    if (page > max) setPage(max);
  }, [filtered.length, limit, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * limit;
    return filtered.slice(start, start + limit);
  }, [filtered, page, limit]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (account: ApiSourceAccount) => {
    setEditing(account);
    setFormOpen(true);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    const name = deleting.display_name;
    removeAccount.mutate(
      { said: deleting.said },
      {
        onSuccess: () => toast.success(`Removed ${name}`),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not remove the account')),
      },
    );
    setDeleting(null);
  };

  const stat = (value: number) => (isLoading ? '—' : String(value));

  return (
    <MediaPageShell
      title="Accounts"
      description="Social handles used as Studio destinations and Radar listen targets — owned brand accounts and tracked competitor or partner handles."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Sources' },
        { label: 'Accounts' },
      ]}
      icon={Users}
      actions={
        <div className="flex flex-wrap gap-2">
          {/* Accounts linked in the provider's dashboard live only there until
              imported — this is the answer to "why can't I see my accounts". */}
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => setImportOpen(true)}
          >
            <Download className="h-4 w-4" /> Import from provider
          </Button>
          <Button type="button" className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add account
          </Button>
        </div>
      }
      stats={[
        { label: 'Accounts', value: stat(accounts.length) },
        {
          label: 'Destinations',
          value: stat(accounts.filter((a) => a.use_as_destination).length),
        },
        { label: 'Listen', value: stat(accounts.filter((a) => a.use_as_listen).length) },
        { label: 'Platforms', value: stat(new Set(accounts.map((a) => a.platform)).size) },
      ]}
    >
      <div className="mb-3 space-y-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, handle, owner, region, topic, notes…"
              className="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-9 text-sm outline-none ring-blue-500 focus:ring-2"
              aria-label="Search accounts"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <FilterToggleGroup
            aria-label="Owned or tracked"
            value={scope}
            onChange={setScope}
            options={[
              { value: 'all', label: 'All' },
              { value: 'owned', label: 'Owned' },
              { value: 'tracked', label: 'Tracked' },
            ]}
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as RoleFilter)}
            className={selectClass}
            aria-label="Role filter"
          >
            <option value="all">Any role</option>
            <option value="destination">Destination</option>
            <option value="listen">Listen</option>
            <option value="both">Destination + listen</option>
            <option value="neither">Neither</option>
          </select>
          <select
            value={tier === 'all' ? 'all' : String(tier)}
            onChange={(e) =>
              setTier(e.target.value === 'all' ? 'all' : (Number(e.target.value) as SourceTier))
            }
            className={selectClass}
            aria-label="Tier filter"
          >
            <option value="all">All tiers</option>
            <option value="1">Tier 1</option>
            <option value="2">Tier 2</option>
            <option value="3">Tier 3</option>
          </select>
          <select
            value={active}
            onChange={(e) => setActive(e.target.value as ActiveFilter)}
            className={selectClass}
            aria-label="Active filter"
          >
            <option value="all">Active + inactive</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
          <select
            value={ownership}
            onChange={(e) => setOwnership(e.target.value as OwnershipFilter)}
            className={selectClass}
            aria-label="Ownership filter"
          >
            {OWNERSHIP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Platforms
            </p>
            <ChipMultiSelect
              options={PLATFORM_CHIP_OPTIONS}
              value={platforms}
              onChange={setPlatforms}
              emptyHint="All platforms"
              aria-label="Platform filters"
            />
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1 pt-4 text-xs text-gray-500">
            <span>
              {isLoading
                ? 'Loading…'
                : filtersActive
                  ? `${filtered.length} match${filtered.length === 1 ? '' : 'es'} of ${accounts.length}`
                  : `${accounts.length} account${accounts.length === 1 ? '' : 's'}`}
            </span>
            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="font-medium text-blue-700 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <TooltipProvider delayDuration={200}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortHeader
                    label="Account"
                    active={sortKey === 'name'}
                    dir={sortDir}
                    onClick={() => toggleSort('name')}
                  />
                  <SortHeader
                    label="Platform"
                    active={sortKey === 'platform'}
                    dir={sortDir}
                    onClick={() => toggleSort('platform')}
                  />
                  <SortHeader
                    label="Type"
                    active={sortKey === 'type'}
                    dir={sortDir}
                    onClick={() => toggleSort('type')}
                  />
                  <SortHeader
                    label="Tier"
                    active={sortKey === 'tier'}
                    dir={sortDir}
                    onClick={() => toggleSort('tier')}
                  />
                  <SortHeader
                    label="Followers"
                    active={sortKey === 'followers'}
                    dir={sortDir}
                    onClick={() => toggleSort('followers')}
                  />
                  <SortHeader
                    label="Coverage"
                    active={sortKey === 'coverage'}
                    dir={sortDir}
                    onClick={() => toggleSort('coverage')}
                  />
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Contacts
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading && (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center text-sm text-gray-500">
                      <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                      Loading accounts…
                    </td>
                  </tr>
                )}

                {!isLoading && isError && (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center text-sm">
                      <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
                      <div className="font-medium text-amber-800">Could not load accounts</div>
                      <div className="mt-1 text-xs text-gray-500">
                        {getApiErrorMessage(error, 'The marketing API did not respond')}
                      </div>
                    </td>
                  </tr>
                )}

                {!isLoading && !isError && accounts.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center text-sm text-gray-500">
                      No accounts registered yet — add the brand handles you publish from, and the
                      competitor or partner handles you want to watch.
                    </td>
                  </tr>
                )}

                {!isLoading && !isError && accounts.length > 0 && filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center text-sm text-gray-500">
                      No accounts match these filters.
                      {filtersActive && (
                        <>
                          {' '}
                          <button
                            type="button"
                            onClick={clearFilters}
                            className="font-medium text-blue-700 hover:underline"
                          >
                            Clear filters
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                )}

                {paged.map((acc) => {
                  const badge = VERIFY_BADGE[acc.verify_state];
                  return (
                    <tr
                      key={acc.said}
                      className={cn('hover:bg-gray-50', !acc.active && 'opacity-60')}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <PlatformLogoBadge platform={acc.platform as SocialPlatform} />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {acc.display_name}{' '}
                              {!!acc.verified && <span className="text-sky-500">✓</span>}
                            </div>
                            <div className="text-xs text-gray-500">{acc.handle}</div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {!!acc.use_as_destination && (
                                <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                                  Destination
                                </span>
                              )}
                              {!!acc.use_as_listen && (
                                <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
                                  Listen
                                </span>
                              )}
                              {!acc.active && (
                                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                                  Inactive
                                </span>
                              )}
                              {badge && (
                                <span
                                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${badge.className}`}
                                  title={acc.verify_detail ?? undefined}
                                >
                                  {badge.label}
                                </span>
                              )}
                            </div>
                            {acc.notes && (
                              <div className="mt-0.5 line-clamp-1 text-[11px] text-gray-400">
                                {acc.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="inline-flex items-center gap-2 text-sm text-gray-600">
                          <PlatformLogoBadge platform={acc.platform as SocialPlatform} size="sm" />
                          {platformLabel(acc.platform)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            acc.owned ? 'bg-violet-50 text-violet-700' : 'bg-sky-50 text-sky-700'
                          }`}
                        >
                          {acc.owned ? 'Owned' : 'Tracked'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">T{acc.tier}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatCompact(acc.followers ?? undefined)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {coverageCounts.isLoading ? '…' : coverageFor(acc.said)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <SourceContactsCell sourceKind="account" sourceId={acc.said} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center justify-end gap-0.5">
                          <ActionIconButton label="Edit" onClick={() => openEdit(acc)}>
                            <Pencil className="h-4 w-4" />
                          </ActionIconButton>
                          <ActionIconButton
                            label="Remove"
                            onClick={() => setDeleting(acc)}
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </ActionIconButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TooltipProvider>
        {!isLoading && !isError && filtered.length > 0 ? (
          <div className="space-y-2 border-t border-gray-100 px-4 py-3">
            <Pagination
              total={filtered.length}
              page={page}
              limit={limit}
              itemsPerPage={limit}
              setPage={setPage}
              setItemsPerPage={(n) => {
                setLimit(n);
                setPage(1);
              }}
              showSelect
              selectOptions={[10, 20, 50]}
              compact
            />
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-400">
              <span>
                {data?.total != null && data.total > accounts.length
                  ? `${data.total} total on server (showing first ${accounts.length})`
                  : '\u00a0'}
              </span>
              <Link
                to="/admin/marketing/sources/providers"
                className="text-blue-700 hover:underline"
              >
                Publishing providers
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex justify-end border-t border-gray-100 px-4 py-2 text-xs">
            <Link
              to="/admin/marketing/sources/providers"
              className="text-blue-700 hover:underline"
            >
              Publishing providers
            </Link>
          </div>
        )}
      </div>

      <ImportProviderAccountsDialog
        open={importOpen}
        provider="zernio"
        onClose={() => setImportOpen(false)}
      />

      <AccountFormDialog
        open={formOpen}
        account={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove account?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && (
                <>
                  Remove <strong>{deleting.display_name}</strong> ({deleting.handle}) from tracked
                  sources
                  {coverageFor(deleting.said) > 0
                    ? `. ${coverageFor(deleting.said)} linked coverage item(s) will keep their
                       content but lose this account link.`
                    : '.'}{' '}
                  Scheduled posts pointing at this account will fail to publish.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MediaPageShell>
  );
};

export default SourcesAccountsPage;
